import React from 'react'
import { Button, Form } from 'react-bootstrap'
import { DropdownData } from 'types/vehicleTypes'
import { HTMLText } from 'components/Share/HtmlText'
import { parseHtml } from 'utils/stringUtils'
import {
  PopoverOverlay,
  PopoverContent,
  PopoverFooter,
  NotificationModalBody,
  NotificationSection,
  NotificationTitle,
  NotifyMeRow,
  NotificationSettings,
  SettingsRow,
  SettingsGroup,
  FormatRow,
  ActionButton
} from '../../pages/SavedSearchPage.styled'
import { useClickOutSide } from 'hooks/useClickOutSide'
import { useGlobalStore } from 'store/useGlobalStore'

export interface NotificationFormData {
  notifyMe: boolean
  emailFormat: boolean
  emailRecurrence: number
  emailDay: number
  emailRecurrenceDuration: number
}

export interface NotificationDropdownData {
  searchFrequencies: DropdownData[] | null
  searchDays: DropdownData[] | null
  searchDurations: DropdownData[] | null
}

interface NotificationModalProps {
  isOpen: boolean
  formData: NotificationFormData
  onFormChange: (formData: NotificationFormData) => void
  dropdownData: NotificationDropdownData
  onSave: () => void
  onCancel: () => void
  saving: boolean
  forwardRef: React.RefObject<HTMLDivElement>
}

export const NotificationModal: React.FC<NotificationModalProps> = ({
  isOpen,
  formData,
  onFormChange,
  dropdownData,
  onSave,
  onCancel,
  saving,
  forwardRef
}) => {
  const { searchFrequencies, searchDays, searchDurations } = dropdownData

  const updateFormData = (updates: Partial<NotificationFormData>) => {
    onFormChange({ ...formData, ...updates })
  }
  const { getLocalText } = useGlobalStore((state) => state)
  useClickOutSide(forwardRef, () => onCancel())

  if (!isOpen) return null

  return (
    <PopoverOverlay ref={forwardRef}>
      <PopoverContent>
        <NotificationModalBody>
          <NotificationSection>
            <NotifyMeRow>
              <span id="notify-title" className="notify-title">
                Notify Me?
              </span>
              <div className="radio-options">
                <Form.Check
                  type="radio"
                  name="NotifyMe"
                  label="YES"
                  checked={formData.notifyMe === true}
                  onChange={() => updateFormData({ notifyMe: true })}
                />
                <Form.Check
                  type="radio"
                  name="NotifyMe"
                  label="NO"
                  checked={formData.notifyMe === false}
                  onChange={() => updateFormData({ notifyMe: false })}
                />
              </div>
            </NotifyMeRow>
          </NotificationSection>

          {formData.notifyMe && (
            <NotificationSection>
              <NotificationTitle>Add Notifications</NotificationTitle>
              <HTMLText value={parseHtml(getLocalText('NOTIFICATION_COMMENT'))} />

              <NotificationSettings>
                <SettingsRow>
                  <SettingsGroup>
                    <label htmlFor="EmailRecurrence">SEND</label>
                    <Form.Control
                      as="select"
                      name="EmailRecurrence"
                      id="EmailRecurrence"
                      onChange={(e) => updateFormData({ emailRecurrence: parseInt(e.target.value) })}
                      value={formData.emailRecurrence}
                    >
                      {searchFrequencies?.map((option) => (
                        <option key={option.ID} value={option.ID}>
                          {option.Description}
                        </option>
                      ))}
                    </Form.Control>
                  </SettingsGroup>

                  {formData.emailRecurrence > 1 && (
                    <SettingsGroup>
                      <label htmlFor="EmailDay">ON</label>
                      <Form.Control
                        as="select"
                        name="EmailDay"
                        id="EmailDay"
                        onChange={(e) => updateFormData({ emailDay: parseInt(e.target.value) })}
                        value={formData.emailDay}
                      >
                        {searchDays?.map((option) => (
                          <option key={option.ID} value={option.ID}>
                            {option.Description}
                          </option>
                        ))}
                      </Form.Control>
                    </SettingsGroup>
                  )}

                  <SettingsGroup>
                    <label htmlFor="EmailRecurrenceDuration">FOR</label>
                    <Form.Control
                      as="select"
                      id="EmailRecurrenceDuration"
                      name="EmailRecurrenceDuration"
                      onChange={(e) => updateFormData({ emailRecurrenceDuration: parseInt(e.target.value) })}
                      value={formData.emailRecurrenceDuration}
                    >
                      {searchDurations?.map((option) => (
                        <option key={option.ID} value={option.ID}>
                          {option.Description}
                        </option>
                      ))}
                    </Form.Control>
                  </SettingsGroup>
                </SettingsRow>

                <FormatRow>
                  <span className="format-title">Format</span>
                  <div className="format-options">
                    <Form.Check
                      type="radio"
                      name="EmailFormat"
                      label="HTML"
                      checked={formData.emailFormat === true}
                      onChange={() => updateFormData({ emailFormat: true })}
                    />
                    <Form.Check
                      type="radio"
                      name="EmailFormat"
                      label="TEXT"
                      checked={formData.emailFormat === false}
                      onChange={() => updateFormData({ emailFormat: false })}
                    />
                  </div>
                </FormatRow>
              </NotificationSettings>
            </NotificationSection>
          )}
        </NotificationModalBody>
        <PopoverFooter>
          <Button variant="light form-btn" onClick={onCancel}>
            Cancel
          </Button>
          <ActionButton onClick={onSave} disabled={saving}>
            Save
          </ActionButton>
        </PopoverFooter>
      </PopoverContent>
    </PopoverOverlay>
  )
}
